import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Building, Plus, Search, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({
      name: '',
      code: '',
      address: '',
      total_budget: 0,
      start_date: '',
      end_date: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/construction/projects`);
      setProjects(res.data);
    } catch (error) {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
      e.preventDefault();
      try {
          await axios.post(`${API}/construction/projects`, newProject);
          toast.success('Obra creada correctamente');
          setIsCreateOpen(false);
          fetchProjects();
      } catch (error) {
          toast.error('Error al crear obra');
      }
  };

  const filteredProjects = projects.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      p.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Obras y Proyectos</h1>
          <p className="text-muted-foreground">Listado maestro de obras en ejecución</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto">
              <Plus className="mr-2 h-4 w-4" /> Nueva Obra
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Registrar Nueva Obra</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Código</label>
                        <Input required value={newProject.code} onChange={e => setNewProject({...newProject, code: e.target.value})} placeholder="OB-001" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Nombre de Obra</label>
                        <Input required value={newProject.name} onChange={e => setNewProject({...newProject, name: e.target.value})} placeholder="Edificio Norte" />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Dirección</label>
                    <Input value={newProject.address} onChange={e => setNewProject({...newProject, address: e.target.value})} placeholder="Av. Principal 123" />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Presupuesto Estimado</label>
                    <Input type="number" required value={newProject.total_budget} onChange={e => setNewProject({...newProject, total_budget: parseFloat(e.target.value)})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Fecha Inicio</label>
                        <Input type="date" value={newProject.start_date} onChange={e => setNewProject({...newProject, start_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Fecha Término</label>
                        <Input type="date" value={newProject.end_date} onChange={e => setNewProject({...newProject, end_date: e.target.value})} />
                    </div>
                </div>
                <Button type="submit" className="w-full">Crear Proyecto</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          className="pl-10" 
          placeholder="Buscar por nombre o código de obra..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando obras...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map(project => (
            <Card key={project.id} className="group hover:border-indigo-500 transition-all cursor-pointer overflow-hidden shadow-sm">
              <CardContent className="p-0">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                            <Building className="h-6 w-6 text-indigo-600" />
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                            project.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                            {project.status === 'active' ? 'En Ejecución' : 'Finalizada'}
                        </span>
                    </div>
                    <h3 className="text-xl font-bold mb-1 group-hover:text-indigo-600 transition-colors">{project.name}</h3>
                    <p className="text-sm text-muted-foreground font-mono mb-4">{project.code}</p>
                    
                    <div className="space-y-2 mb-6">
                        <div className="flex items-center text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4 mr-2" /> {project.address || 'Sin dirección'}
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4 mr-2" /> {project.start_date || '--'} al {project.end_date || '--'}
                        </div>
                    </div>
                    
                    <Button asChild variant="outline" className="w-full group-hover:bg-indigo-600 group-hover:text-white transition-all">
                        <Link to={`/projects/${project.id}`}>Ver Detalle de Obra</Link>
                    </Button>
                </div>
                <div className="h-1 bg-indigo-100 dark:bg-indigo-900 w-full overflow-hidden">
                    <div className="h-full bg-indigo-600 w-1/3 group-hover:w-full transition-all duration-700"></div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredProjects.length === 0 && (
            <div className="col-span-full py-12 text-center border-2 border-dashed rounded-xl text-muted-foreground">
                No se encontraron obras que coincidan con la búsqueda.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
